import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateRange from 'components/Form/DateRange';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import { SubstanceAlcoholSupplementalCounselingItemModel } from 'forms/sections/substance/alcohol/counseling/supplemental/types';
import Comments from 'components/Form/Comments';

interface Props
  extends ValidationElementProps,
  Partial<SubstanceAlcoholSupplementalCounselingItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
}

export default class AlcoholCounseling extends ValidationElement<Props> {
  update = (values: Partial<SubstanceAlcoholSupplementalCounselingItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        CounselingDates: this.props.CounselingDates,
        TreatmentProviderName: this.props.TreatmentProviderName,
        TreatmentProviderAddress: this.props.TreatmentProviderAddress,
        ConsultationComment: this.props.ConsultationComment,
        ...values,
      });
    }
  };

  updateCounselingDates = (
    values: SubstanceAlcoholSupplementalCounselingItemModel['CounselingDates']
  ) => {
    this.update({ CounselingDates: values });
  };

  updateTreatmentProviderName = (
    values: SubstanceAlcoholSupplementalCounselingItemModel['TreatmentProviderName']
  ) => {
    this.update({ TreatmentProviderName: values });
  };

  updateTreatmentProviderAddress = (
    values: SubstanceAlcoholSupplementalCounselingItemModel['TreatmentProviderAddress']
  ) => {
    this.update({ TreatmentProviderAddress: values });
  };

  updateConsultationComment = (
    values: SubstanceAlcoholSupplementalCounselingItemModel['ConsultationComment']
  ) => {
    this.update({ ConsultationComment: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div className="any-counseling">
        <Field
          title={i18n.t('substance.alcohol.anyCounseling.heading.counselingDates')}
          attribute={`${attribute}.CounselingDates`}
          help="substance.alcohol.anyCounseling.help.counselingDates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="CounselingDates"
            attribute={`${attribute}.CounselingDates`}
            className="counseling-dates"
            minDateEqualTo
            {...this.props.CounselingDates}
            onUpdate={this.updateCounselingDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            allowPresent={false}
          />
        </Field>
        <Field
          title={i18n.t('substance.alcohol.anyCounseling.heading.treatmentProviderName')}
          attribute={`${attribute}.TreatmentProviderName`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="TreatmentProviderName"
            attribute={`${attribute}.TreatmentProviderName`}
            className="treatment-provider-name"
            {...this.props.TreatmentProviderName}
            onUpdate={this.updateTreatmentProviderName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.alcohol.anyCounseling.heading.treatmentProviderAddress')}
          attribute={`${attribute}.TreatmentProviderAddress`}
          adjustFor="address"
          help="substance.alcohol.anyCounseling.help.treatmentProviderAddress"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            name="TreatmentProviderAddress"
            attribute={`${attribute}.TreatmentProviderAddress`}
            className="provider-address"
            {...this.props.TreatmentProviderAddress}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Provider"
            showPostOffice
            dispatch={this.props.dispatch}
            onUpdate={this.updateTreatmentProviderAddress}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t('substance.alcohol.anyCounseling.heading.consultationComment')}
          name="ConsultationComment"
          attribute={`${attribute}.ConsultationComment`}
          className="consultation-comment"
          {...this.props.ConsultationComment}
          onUpdate={this.updateConsultationComment}
          onError={this.props.onError}
          required={false}
        >
        </Comments>
      </div>
    );
  }
}

AlcoholCounseling.defaultProps = {
  ConsultationComment: {},
  addressBooks: {},
  dispatch: (action) => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
